import type { CharacterConfig, DrinkCue } from '../config/character.config';

/**
 * Extra life = "Prost!": the runner stops on the spot and drinks a beer (ANIM_Drink) while the world
 * waits – the simulation is frozen, rendering and the clip go on, so the reward never costs ground.
 * Requested by the extra-life event; it starts as soon as the runner can stand (on the ground, not in
 * a lane change) and is dropped if that does not happen within `pendingSeconds`.
 */
export class DrinkBreak {
  /** Seconds a requested break may still wait for the runner; < 0 = nothing requested. */
  private pendingFor = -1;
  /** Seconds into the running break; < 0 = not drinking. */
  private elapsed = -1;
  /** Sound cues reached by the last update (clip frames passed). */
  private readonly reached: DrinkCue[] = [];

  constructor(private readonly config: CharacterConfig['drinkBreak']) {}

  /** Length of the break = length of the clip (its first to its last key). */
  get duration(): number {
    return (this.config.clipFrames - 1) / this.config.clipFps;
  }

  get active(): boolean {
    return this.elapsed >= 0;
  }

  /** 0..1 through the break, null while not drinking. */
  get progress(): number | null {
    return this.active ? Math.min(1, this.elapsed / this.duration) : null;
  }

  /** Sound cues the clip passed in the last update, in order. */
  get cues(): readonly DrinkCue[] {
    return this.reached;
  }

  /** An extra life was gained – one drink, even if several lives come at once. */
  request(): void {
    if (!this.active) this.pendingFor = this.config.pendingSeconds;
  }

  /**
   * Per rendered frame of a run: counts a running break down, or starts a requested one when the runner
   * `canStart`. Returns true while the world has to wait.
   */
  update(dt: number, canStart: boolean): boolean {
    this.reached.length = 0;
    if (this.active) {
      const before = this.frameAt(this.elapsed);
      this.elapsed += dt;
      this.collectCues(before, this.frameAt(this.elapsed));
      if (this.elapsed >= this.duration) this.elapsed = -1;
      return this.active;
    }
    if (this.pendingFor < 0) return false;
    if (canStart) {
      this.pendingFor = -1;
      this.elapsed = 0;
      return true;
    }
    this.pendingFor -= dt;
    return false;
  }

  reset(): void {
    this.pendingFor = -1;
    this.elapsed = -1;
    this.reached.length = 0;
  }

  private frameAt(seconds: number): number {
    return 1 + seconds * this.config.clipFps;
  }

  /** Cues on clip frames in (from, to]. */
  private collectCues(from: number, to: number): void {
    const { open, gulp, ahh, prost } = this.config.cues;
    const timeline: [DrinkCue, number][] = [['open', open], ...gulp.map((f): [DrinkCue, number] => ['gulp', f]), ['ahh', ahh], ['prost', prost]];
    for (const [cue, frame] of timeline) if (frame > from && frame <= to) this.reached.push(cue);
  }
}
